import React from 'react';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography } from '@material-ui/core';
import { Info as InfoIcon } from '@material-ui/icons';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import RichTextContent from 'components/RichTextContent/RichTextContent';
import action_library_messages from 'messages/action_library_messages';

import { ActionLibraryInstanceFormValues } from '../../ActionLibraryInstanceForm.types';

import useStyles from './ActionLibraryInstanceSummaryDialog.styles';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  values: ActionLibraryInstanceFormValues;
  isSubmitting: boolean;
};

const ActionLibraryInstanceSummaryDialog: React.FC<Props> = ({ open, onClose, onConfirm, values, isSubmitting }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const template = values.selectedTemplate;

  const formatDate = (dateValue: string | Date | null | undefined) => {
    if (!dateValue) return '-';

    let date: Date;

    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      // If already in YYYY-MM-DD format, return as-is
      if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateValue;
      }

      // Try parsing the date string
      date = new Date(dateValue);

      // Check if date is valid
      if (Number.isNaN(date.getTime())) {
        return '-';
      }
    } else {
      return '-';
    }

    // Format as YYYY-MM-DD using date-fns (same as parseDate helper)
    try {
      return format(date, 'yyyy-MM-dd');
    } catch (e) {
      return '-';
    }
  };

  const getStepStatus = (stepId: number, originalMandatory?: boolean) => {
    const override = values.stepOverrides.find(o => o.originalStepId === stepId);
    const isMandatory = override ? override.mandatory : !!originalMandatory;
    return isMandatory
      ? t(action_library_messages.instance.summary_dialog.mandatory)
      : t(action_library_messages.instance.summary_dialog.optional);
  };

  return (
    <Dialog fullWidth maxWidth='xs' onClose={onClose} open={open}>
      <DialogTitle>{t(action_library_messages.instance.summary_dialog.title)}</DialogTitle>
      <DialogContent dividers>
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant='subtitle1'>
            {t(action_library_messages.instance.summary_dialog.general_info)}
          </Typography>
          <div className={classes.row}>
            <Typography className={classes.label} variant='body2'>
              {t(action_library_messages.instance.summary_dialog.template)}
            </Typography>
            <Box alignItems='center' display='flex'>
              <Typography className={classes.value} style={{ marginRight: 8 }} variant='body2'>
                {template?.name || '-'}
              </Typography>
              {template && (
                <Tooltip
                  title={
                    <Box style={{ maxWidth: 400 }}>
                      <Typography color='inherit' style={{ marginBottom: 8 }} variant='subtitle2'>
                        {t(action_library_messages.instance.summary_dialog.template_details)}
                      </Typography>
                      <div style={{ marginBottom: 6 }}>
                        <b>{t(action_library_messages.instance.summary_dialog.area)}</b> {template.areaDisplay}{' '}
                        {template.subAreaData ? `/ ${template.subAreaData.name}` : ''}
                      </div>
                      <div style={{ marginBottom: 6 }}>
                        <b>{t(action_library_messages.instance.summary_dialog.purpose)}</b> {template.purposeDisplay}
                      </div>
                      <div style={{ marginBottom: 6 }}>
                        <b>{t(action_library_messages.instance.summary_dialog.criticality)}</b> {template.criticalityDisplay}
                      </div>
                      {template.reference && (
                        <div style={{ marginBottom: 6 }}>
                          <b>{t(action_library_messages.instance.summary_dialog.reference)}</b> {template.reference}
                        </div>
                      )}
                      {template.reportedTo && (
                        <div style={{ marginBottom: 6 }}>
                          <b>Reported To:</b> {template.reportedTo}
                        </div>
                      )}
                      {template.validityPeriodMonths && (
                        <div style={{ marginBottom: 6 }}>
                          <b>Validity Period:</b> {template.validityPeriodMonths} months
                        </div>
                      )}
                      {template.assetCategories && template.assetCategories.length > 0 && (
                        <div style={{ marginBottom: 6 }}>
                          <b>Asset Categories:</b> {template.assetCategories.map(cat => cat.name).join(', ')}
                        </div>
                      )}
                      {template.ownerTypeDisplay && (
                        <div style={{ marginBottom: 6 }}>
                          <b>Owner Type:</b> {template.ownerTypeDisplay}
                        </div>
                      )}
                      {template.statusDisplay && (
                        <div>
                          <b>Status:</b> {template.statusDisplay}
                        </div>
                      )}
                    </Box>
                  }
                >
                  <InfoIcon color='action' fontSize='small' />
                </Tooltip>
              )}
            </Box>
          </div>
          <div className={classes.row}>
            <Typography className={classes.label} variant='body2'>
              {t(action_library_messages.instance.summary_dialog.instance_name)}
            </Typography>
            <Typography className={classes.value} variant='body2'>
              {values.instanceName || '-'}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography className={classes.label} variant='body2'>
              {t(action_library_messages.instance.summary_dialog.reported_to)}
            </Typography>
            <Typography className={classes.value} variant='body2'>
              {values.reportedTo || '-'}
            </Typography>
          </div>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant='subtitle1'>
            {t(action_library_messages.instance.summary_dialog.time_parameters)}
          </Typography>
          <div className={classes.row}>
            <Typography className={classes.label} variant='body2'>
              {t(action_library_messages.instance.summary_dialog.deadline)}
            </Typography>
            <Typography className={classes.value} variant='body2'>
              {values.deadlineDate && values.deadlineTime ? `${formatDate(values.deadlineDate)}` : formatDate(values.deadlineDate) || '-'}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography className={classes.label} variant='body2'>
              {t(action_library_messages.instance.summary_dialog.reminder)}
            </Typography>
            <Typography className={classes.value} variant='body2'>
              {values.reminderDate && values.reminderTime ? `${formatDate(values.reminderDate)}` : formatDate(values.reminderDate) || '-'}
            </Typography>
          </div>
        </Box>

        <Box className={classes.section}>
          <Box alignItems='center' display='flex' mb={1}>
            <Typography className={classes.sectionTitle} style={{ marginBottom: 0, marginRight: 8 }} variant='subtitle1'>
              {t(action_library_messages.instance.summary_dialog.special_instructions)}
            </Typography>
          </Box>
          <Typography variant='body2'>
            {values.specialInstruction ? <RichTextContent content={values.specialInstruction} /> : '-'}
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant='subtitle1'>
            {t(action_library_messages.instance.summary_dialog.steps)} ({template?.steps?.length || 0})
          </Typography>
          {template?.steps && template.steps.length > 0 ? (
            <ul className={classes.assetList}>
              {(template.steps || []).map(step => (
                <li key={step.id} style={{ marginBottom: 8 }}>
                  <strong>{step.title}</strong> <strong>({getStepStatus(step.id, step.mandatory)})</strong>
                  {step.shortDescription && (
                    <div style={{ marginLeft: 16, marginTop: 4, fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                      <RichTextContent content={step.shortDescription} />
                    </div>
                  )}
                  {step.criticalityDisplay && (
                    <div style={{ marginLeft: 16, marginTop: 2, fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.5)' }}>
                      Criticality: {step.criticalityDisplay}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <Typography color='textSecondary' variant='body2'>
              No steps defined
            </Typography>
          )}
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant='subtitle1'>
            {t(action_library_messages.instance.summary_dialog.selected_assets)} ({values.selectedAssets.length})
          </Typography>
          {values.selectedAssets.length > 0 ? (
            <ul className={classes.assetList}>
              {values.selectedAssets.map(asset => (
                <li key={asset.id} style={{ marginBottom: 8 }}>
                  <strong>{asset.name}</strong>
                  <div style={{ marginLeft: 16, marginTop: 4, fontSize: '0.875rem' }}>
                    {t(action_library_messages.instance.summary_dialog.owner_label)}:{' '}
                    {asset.owner?.fullName || t(action_library_messages.instance.summary_dialog.not_available)}
                  </div>
                  {asset.owner?.email && (
                    <div style={{ marginLeft: 16, marginTop: 2, fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                      Email: {asset.owner.email}
                    </div>
                  )}
                  {asset.category?.name && (
                    <div style={{ marginLeft: 16, marginTop: 2, fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                      Category: {asset.category.name}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <Typography color='textSecondary' variant='body2'>
              {t(action_library_messages.instance.summary_dialog.no_assets_selected)}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={isSubmitting} onClick={onClose}>
          {t(action_library_messages.instance.summary_dialog.cancel)}
        </Button>
        <Button color='primary' disabled={isSubmitting} onClick={onConfirm} variant='contained'>
          {isSubmitting
            ? t(action_library_messages.instance.summary_dialog.creating)
            : t(action_library_messages.instance.summary_dialog.create_instance)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActionLibraryInstanceSummaryDialog;
